import { create } from 'zustand';
import { useConnectionStore } from './connection.store';

export interface WebSocketSubscription {
  id: string;
  channel: string;
  event: string;
  callback: (data: unknown) => void;
  echoCallback?: (data: unknown) => void;
}

export interface WebSocketChannel {
  name: string;
  type: 'public' | 'private' | 'presence';
  subscribed: boolean;
  subscribedAt?: number;
}

interface SubscriptionState {
  // Channels and subscriptions
  channels: Map<string, WebSocketChannel>;
  subscriptions: Map<string, WebSocketSubscription>;
  channelSubscriptions: Map<string, Set<string>>;

  // Subscription management
  listen: (
    channel: string,
    event: string,
    callback: (data: unknown) => void,
    channelType?: 'public' | 'private' | 'presence'
  ) => string;
  stopListening: (subscriptionId: string) => void;
  subscribe: (
    channel: string,
    type?: 'public' | 'private' | 'presence'
  ) => void;
  unsubscribe: (channel: string) => void;
  isSubscribed: (channel: string) => boolean;
  getChannel: (channel: string) => WebSocketChannel | undefined;

  // Cleanup
  clearAll: () => void;

  // Computed getters
  getActiveChannels: () => WebSocketChannel[];
  getSubscriptionCount: () => number;
}

export const useSubscriptionStore = create<SubscriptionState>((set, get) => ({
  channels: new Map(),
  subscriptions: new Map(),
  channelSubscriptions: new Map(),

  // Subscription management
  listen: (
    channel: string,
    event: string,
    callback: (data: unknown) => void,
    channelType?: 'public' | 'private' | 'presence'
  ) => {
    const id = `${channel}:${event}:${Date.now()}`;
    const subscription: WebSocketSubscription = {
      id,
      channel,
      event,
      callback,
    };

    // Add subscription to internal tracking
    set((state) => {
      const newSubscriptions = new Map(state.subscriptions);
      newSubscriptions.set(id, subscription);

      const newChannelSubs = new Map(state.channelSubscriptions);
      if (!newChannelSubs.has(channel)) {
        newChannelSubs.set(channel, new Set());
      }
      newChannelSubs.get(channel)?.add(id);

      return {
        subscriptions: newSubscriptions,
        channelSubscriptions: newChannelSubs,
      };
    });

    // Get Echo instance from connection store
    const connectionStore = useConnectionStore.getState();

    // Set up actual Echo listener if connected
    if (connectionStore.echo) {
      // Determine channel type: if not specified, auto-detect based on channel name
      const type =
        channelType || (channel.startsWith('user.') ? 'private' : 'public');

      // Subscribe to the appropriate channel type
      let echoChannel;
      if (type === 'private') {
        // For private channels, Echo expects the channel name WITHOUT 'private-' prefix
        // Echo will add 'private-' prefix automatically
        echoChannel = connectionStore.echo.private(channel);
      } else if (type === 'presence') {
        echoChannel = connectionStore.echo.join(channel);
      } else {
        echoChannel = connectionStore.echo.channel(channel);
      }

      // Listen for the event - note the dot prefix for custom events
      const eventName = event.startsWith('.') ? event : `.${event}`;

      // Create wrapped callback for Echo that we can reference for cleanup
      const echoCallback = (data: unknown) => {
        // Ably SDK parses JSON automatically - we receive parsed data here
        callback(data);
      };

      // Store the Echo callback reference in subscription for cleanup
      set((state) => {
        const newSubscriptions = new Map(state.subscriptions);
        const sub = newSubscriptions.get(id);
        if (sub) {
          sub.echoCallback = echoCallback;
          newSubscriptions.set(id, sub);
        }
        return { subscriptions: newSubscriptions };
      });

      echoChannel.listen(eventName, echoCallback);
    }

    return id;
  },

  stopListening: (subscriptionId: string) => {
    const state = get();
    const subscription = state.subscriptions.get(subscriptionId);

    if (subscription) {
      // Get Echo instance from connection store
      const connectionStore = useConnectionStore.getState();

      // Stop Echo listener if connected
      if (connectionStore.echo && subscription.echoCallback) {
        // Determine channel type based on channel name
        const type = subscription.channel.startsWith('user.')
          ? 'private'
          : 'public';

        // Get the appropriate channel instance
        let echoChannel;
        if (type === 'private') {
          echoChannel = connectionStore.echo.private(subscription.channel);
        } else {
          echoChannel = connectionStore.echo.channel(subscription.channel);
        }

        // Stop listening to the specific callback - add dot prefix if needed
        const eventName = subscription.event.startsWith('.')
          ? subscription.event
          : `.${subscription.event}`;
        // Pass callback to stopListening to remove only this specific listener
        echoChannel.stopListening(eventName, subscription.echoCallback);
      }

      // Remove from internal tracking
      set((state) => {
        const newSubscriptions = new Map(state.subscriptions);
        newSubscriptions.delete(subscriptionId);

        const newChannelSubs = new Map(state.channelSubscriptions);
        const channelSubs = newChannelSubs.get(subscription.channel);
        if (channelSubs) {
          channelSubs.delete(subscriptionId);
          if (channelSubs.size === 0) {
            newChannelSubs.delete(subscription.channel);
          }
        }

        return {
          subscriptions: newSubscriptions,
          channelSubscriptions: newChannelSubs,
        };
      });
    }
  },

  subscribe: (
    channel: string,
    type: 'public' | 'private' | 'presence' = 'public'
  ) => {
    const channelInfo: WebSocketChannel = {
      name: channel,
      type,
      subscribed: true,
      subscribedAt: Date.now(),
    };

    set((state) => {
      const newChannels = new Map(state.channels);
      newChannels.set(channel, channelInfo);
      return { channels: newChannels };
    });

    // Get Echo instance from connection store
    const connectionStore = useConnectionStore.getState();

    // Subscribe via Echo if connected
    if (connectionStore.echo) {
      if (type === 'private') {
        connectionStore.echo.private(channel);
      } else if (type === 'presence') {
        connectionStore.echo.join(channel);
      } else {
        connectionStore.echo.channel(channel);
      }
    }
  },

  unsubscribe: (channel: string) => {
    const state = get();

    // Remove all subscriptions for this channel
    const channelSubs = state.channelSubscriptions.get(channel);
    if (channelSubs) {
      channelSubs.forEach((id) => {
        state.stopListening(id);
      });
    }

    // Remove channel
    set((state) => {
      const newChannels = new Map(state.channels);
      newChannels.delete(channel);
      return { channels: newChannels };
    });

    // Get Echo instance from connection store
    const connectionStore = useConnectionStore.getState();

    // Leave channel via Echo if connected
    if (connectionStore.echo) {
      connectionStore.echo.leave(channel);
    }
  },

  isSubscribed: (channel: string) => {
    return get().channels.has(channel);
  },

  getChannel: (channel: string) => {
    return get().channels.get(channel);
  },

  clearAll: () => {
    set({
      channels: new Map(),
      subscriptions: new Map(),
      channelSubscriptions: new Map(),
    });
  },

  // Computed getters
  getActiveChannels: () => {
    return Array.from(get().channels.values()).filter((ch) => ch.subscribed);
  },

  getSubscriptionCount: () => {
    return get().subscriptions.size;
  },
}));

// Auto-clear subscriptions on connection drop. Previously this ran from
// `connection.store.disconnect()` calling back into us, which closed a
// madge cycle (`connection.store ↔ subscription.store`). Now the
// dependency arrow is one-way: subscription depends on connection.
//
// Safe at module load — `subscribe(cb)` only fires on subsequent state
// changes, and no subscriptions exist before this module is imported, so
// nothing leaks if `disconnect()` runs before the first listener is
// added.
useConnectionStore.subscribe((state, prev) => {
  if (prev.isConnected && !state.isConnected) {
    useSubscriptionStore.getState().clearAll();
  }
});
